import React, {type ReactNode, useMemo} from 'react';
import {ResizableBox} from 'react-resizable';
import {twMerge} from 'tailwind-merge';

import {useIsMobile} from '../../../util/panelbankFlow';
import {useResizableWorkspaceSection} from './useResizableWorkspaceSection';
import {DEFAULT_SECTION_GAP} from './utils';
import {useWorkspaceSectionLayoutConfigs} from './WorkspaceSectionLayoutConfigs';

const RESIZABLE_SECTION_DRAGGABLE_OPTS = {
  axis: 'y',
  // This is an arbitrary selected value.
  // Setting this increment allows a smoother dragging experience
  // https://github.com/react-grid-layout/react-draggable/blob/eefb704f8d5e706b2f1a697b955ea523dbc80991/lib/DraggableCore.js#L122
  grid: [10, 10],
};

type ResizableWorkspaceSectionProps = {
  children: ReactNode;
  className?: string;
  height: number;
  isResizing: boolean;
  panelsPerPage: number;
  setResizingSectionHeight: (height: number | null) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
};

export const ResizableWorkspaceSection = ({
  children,
  className,
  height,
  isResizing,
  panelsPerPage,
  setResizingSectionHeight,
  setRowsPerPage,
}: ResizableWorkspaceSectionProps) => {
  const isMobileLayout = useIsMobile();
  const {panelHeight, maxRowsInSection} = useWorkspaceSectionLayoutConfigs();

  const {handleOnResize, handleResizeStop} = useResizableWorkspaceSection({
    panelsPerPage,
    setResizingSectionHeight,
    setRowsPerPage,
  });

  const {
    minConstraints,
    maxConstraints,
  }: {
    minConstraints: [width: number, height: number];
    maxConstraints: [width: number, height: number];
  } = useMemo(() => {
    return {
      // A section cannot be smaller than the current height of a panel.
      // There is always a minimum at least one row in a section if a panel is present.
      minConstraints: [0, panelHeight],
      // We restrain the max height based on the number of panels that are in a section.
      maxConstraints: [
        0,
        maxRowsInSection * (panelHeight + DEFAULT_SECTION_GAP) -
          DEFAULT_SECTION_GAP,
      ],
    };
  }, [panelHeight, maxRowsInSection]);

  return (
    <ResizableBox
      data-testid="resizable-workspace-section"
      axis="y"
      className={className}
      draggableOpts={RESIZABLE_SECTION_DRAGGABLE_OPTS}
      height={height}
      handle={
        // Give ample height for user to trigger resize handle to appear at the bottom of the section.
        <div
          className={twMerge(
            // Do not allow resizing sections on mobile for a better user experience.
            // This affects section pagination and flow config, so let's not enable this until we have time to
            // properly support it.
            isMobileLayout && 'hidden',
            'absolute h-16 w-full',
            'opacity-0',
            // To provide a smoother dragging experience only show the line when not resizing.
            // Rely on the cursor icon to indicate if resizing is happening for now.
            !isResizing && 'hover:cursor-row-resize hover:opacity-100'
          )}
          data-testid="resizable-workspace-section-handle"
          style={{bottom: `-${DEFAULT_SECTION_GAP}px`}}>
          <div
            className={twMerge(
              // There's a default background image that the library uses that we want to hide. We want to display a horizontal line instead.
              'bg-none',
              // Position the handle at the bottom of the section in between the 16px gap and account for the height of the handle.
              'relative bottom-[-6px] ',
              'rounded-xs h-2 bg-moon-350 dark:bg-moon-450'
            )}
          />
        </div>
      }
      minConstraints={minConstraints}
      maxConstraints={maxConstraints}
      onResize={handleOnResize}
      onResizeStop={handleResizeStop}>
      {children}
    </ResizableBox>
  );
};
